/**
 * AiChat Store - Model, Action, update, and effects for the AI chat component
 *
 * This module defines the ai-chat domain which is integrated into app-store
 * as a scoped store. The component receives a scoped store that maps state
 * and transforms actions.
 *
 * State managed:
 * - Messages
 * - Loading state
 * - Delete confirmation alert
 */
import { type Store, updateUnknown } from "refrakt";
import { type Ulid } from "../../../models/ulid.ts";
import * as Logger from "../../services/logger.ts";
import {
  type ChatMessage,
  type UserChatMessage,
  parseUserChatMessage,
} from "../../../models/chat-message.ts";
import { toMapById, updateMapById } from "../../../util/identifiable.ts";
import { randomThinking } from "../../util/thinking.ts";
import {
  getMessages,
  postUserMessage,
  saveThinkingSummaries,
  upsertConversation,
} from "../../services/conversations.ts";
import {
  generateTitle,
  generateThinkingSummary,
} from "../../services/generate.ts";
import { flattenUIMessagesToString } from "../../../ai-sdk/utils.ts";
import {
  countClosedThinkingGroups,
  groupPartsIntoSegments,
  formatThinkingGroupPartsForPrompt,
} from "../../../ai-sdk/thinking-parts.ts";
import { nowIsoString } from "../../../util/date.ts";
import { isEmpty } from "../../../util/text.ts";
import { findLatestToolCallId } from "../../../util/tool-call.ts";

const logger = Logger.create({ source: "client/ai-chat" });

export type Model = {
  conversationTitle: string;
  /** Chat messages by ID */
  messages: Map<string, ChatMessage>;
  /** Loading indicator text */
  loadingText: string;
  /** Whether we're loading the initial conversation */
  isLoading: boolean;
  /** Current conversation ID */
  conversationId: string | undefined;
  /** Current project ID */
  projectId: Ulid | undefined;
  /** Current agent ID */
  agentId: Ulid | undefined;
  /** Whether delete confirmation alert is shown */
  showDeleteProjectAlert: boolean;
  /** Timestamp of last message update (for triggering scroll effects) */
  messagesLastUpdated: number;
  /** ID of the most recent tool call across all messages */
  latestToolCallId: string | undefined;
  /** Tool call IDs that have already triggered a note-list refresh */
  seenListInvalidatingToolCallIds: Set<string>;
  /** Haiku-generated summaries for thinking groups, keyed by message ID */
  thinkingSummaries: Map<string, string[]>;
};

export const model = ({
  conversationTitle = "Untitled",
  messages = new Map(),
  loadingText = randomThinking("Thinking"),
  isLoading = false,
  conversationId = undefined,
  projectId = undefined,
  agentId = undefined,
  showDeleteProjectAlert = false,
  messagesLastUpdated = 0,
  latestToolCallId = undefined,
  seenListInvalidatingToolCallIds = new Set(),
  thinkingSummaries = new Map(),
}: Partial<Model> = {}): Model => ({
  conversationTitle,
  messages,
  loadingText,
  isLoading,
  conversationId,
  projectId,
  agentId,
  showDeleteProjectAlert,
  messagesLastUpdated,
  latestToolCallId,
  seenListInvalidatingToolCallIds,
  thinkingSummaries,
});

/**
 * Dispatched when a new conversation is created (from `fxCreateConversation`
 * in app-store) to initialize chat state and optionally submit the first
 * message. Not used when navigating to an existing conversation — that goes
 * through `set-conversation` instead.
 */
export type ResumeConversationAction = {
  type: "resume-conversation";
  conversationId: string;
  projectId: string;
  agentId: string;
  title: string;
  messages: ChatMessage[];
  /** Optional message to submit immediately (displayed optimistically and sent to API) */
  submitMessage?: UserChatMessage;
  accessToken: string | undefined;
};

export type SetConversationAction = {
  type: "set-conversation";
  conversationId: string;
  projectId: string;
  /** Whether to load messages from API */
  load: boolean;
  accessToken: string | undefined;
};

// Internal actions - used by effects
export type SetConversationInfoAction = {
  type: "set-conversation-info";
  title: string;
  agentId: string;
};
export type SetMessagesAction = {
  type: "set-messages";
  messages: ChatMessage[];
};
export type StreamMessageAction = {
  type: "stream-message";
  message: ChatMessage;
  accessToken: string | undefined;
};
export type AppendMessagesAction = {
  type: "append-messages";
  messages: ChatMessage[];
};
export type SetLoadingAction = {
  type: "set-loading";
  isLoading: boolean;
};
export type SubmitAction = {
  type: "submit";
  content: string;
  accessToken: string | undefined;
};
export type ToggleDeleteAlertAction = {
  type: "toggle-delete-alert";
  show: boolean;
};
export type ErrorAction = {
  type: "error";
  message: string;
  error: string;
};
export type SetThinkingSummariesAction = {
  type: "set-thinking-summaries";
  messageId: string;
  summaries: string[];
};

export type RequestLoadNoteListsAction = {
  type: "request-load-note-lists";
};

export type AddListInvalidatingToolCallIdsAction = {
  type: "add-list-invalidating-tool-call-ids";
  ids: string[];
};

export type Action =
  | ResumeConversationAction
  | SetConversationAction
  | SetConversationInfoAction
  | SetMessagesAction
  | StreamMessageAction
  | AppendMessagesAction
  | SetLoadingAction
  | SubmitAction
  | ToggleDeleteAlertAction
  | ErrorAction
  | SetThinkingSummariesAction
  | RequestLoadNoteListsAction
  | AddListInvalidatingToolCallIdsAction;

/**
 * Helper to update messages map with latest tool call ID and timestamp.
 * Reduces duplication across update functions.
 */
const withUpdatedMessages = (
  messages: Map<string, ChatMessage>,
): Pick<Model, "messages" | "latestToolCallId" | "messagesLastUpdated"> => ({
  messages,
  latestToolCallId: findLatestToolCallId(messages.values()),
  messagesLastUpdated: Date.now(),
});

export const updateResumeConversation = (
  state: Model,
  action: ResumeConversationAction,
): Model => {
  // Append optimistic user message if submitting
  const messages: ChatMessage[] = action.submitMessage
    ? [...action.messages, action.submitMessage]
    : action.messages;

  const messagesMap = toMapById(messages);
  return {
    ...state,
    conversationId: action.conversationId,
    projectId: action.projectId,
    agentId: action.agentId,
    conversationTitle: action.title,
    ...withUpdatedMessages(messagesMap),
    isLoading: action.submitMessage !== undefined,
    showDeleteProjectAlert: false,
    seenListInvalidatingToolCallIds: new Set(),
    thinkingSummaries: new Map(),
  };
};

export const updateSetConversation = (
  state: Model,
  action: SetConversationAction,
): Model => {
  if (action.load) {
    return {
      ...state,
      conversationId: action.conversationId,
      projectId: action.projectId,
      messages: new Map(),
      isLoading: true,
      showDeleteProjectAlert: false,
      seenListInvalidatingToolCallIds: new Set(),
      thinkingSummaries: new Map(),
    };
  } else {
    return {
      ...state,
      conversationId: action.conversationId,
      projectId: action.projectId,
      showDeleteProjectAlert: false,
    };
  }
};

export const updateSetConversationInfo = (
  state: Model,
  action: SetConversationInfoAction,
): Model => ({
  ...state,
  conversationTitle: action.title,
  agentId: action.agentId,
});

export const updateSetMessages = (
  state: Model,
  action: SetMessagesAction,
): Model => {
  const messagesMap = toMapById(action.messages);
  return {
    ...state,
    ...withUpdatedMessages(messagesMap),
    isLoading: false,
  };
};

export const updateStreamMessage = (
  state: Model,
  action: StreamMessageAction,
): Model => {
  const updatedMessages = updateMapById(state.messages, [action.message]);
  return {
    ...state,
    ...withUpdatedMessages(updatedMessages),
  };
};

export const updateAppendMessages = (
  state: Model,
  action: AppendMessagesAction,
): Model => {
  const updatedMessages = updateMapById(state.messages, action.messages);
  return {
    ...state,
    ...withUpdatedMessages(updatedMessages),
  };
};

export const updateSetLoading = (
  state: Model,
  action: SetLoadingAction,
): Model => ({
  ...state,
  isLoading: action.isLoading,
  loadingText: randomThinking("Thinking"),
});

export const updateToggleDeleteAlert = (
  state: Model,
  action: ToggleDeleteAlertAction,
): Model => ({
  ...state,
  showDeleteProjectAlert: action.show,
});

export const updateError = (state: Model, action: ErrorAction): Model => {
  logger.warn("AiChat error", { message: action.message, error: action.error });
  return state;
};

export const updateSetThinkingSummaries = (
  state: Model,
  action: SetThinkingSummariesAction,
): Model => {
  const updated = new Map(state.thinkingSummaries);
  updated.set(action.messageId, action.summaries);
  return { ...state, thinkingSummaries: updated };
};

export const updateAddListInvalidatingToolCallIds = (
  state: Model,
  action: AddListInvalidatingToolCallIdsAction,
): Model => ({
  ...state,
  seenListInvalidatingToolCallIds: new Set([
    ...state.seenListInvalidatingToolCallIds,
    ...action.ids,
  ]),
});

export const update = (state: Model, action: Action): Model => {
  switch (action.type) {
    case "resume-conversation":
      return updateResumeConversation(state, action);
    case "set-conversation":
      return updateSetConversation(state, action);
    case "set-conversation-info":
      return updateSetConversationInfo(state, action);
    case "set-messages":
      return updateSetMessages(state, action);
    case "stream-message":
      return updateStreamMessage(state, action);
    case "append-messages":
      return updateAppendMessages(state, action);
    case "set-loading":
      return updateSetLoading(state, action);
    case "submit":
      return state;
    case "toggle-delete-alert":
      return updateToggleDeleteAlert(state, action);
    case "error":
      return updateError(state, action);
    case "set-thinking-summaries":
      return updateSetThinkingSummaries(state, action);
    case "request-load-note-lists":
      return state;
    case "add-list-invalidating-tool-call-ids":
      return updateAddListInvalidatingToolCallIds(state, action);
    default:
      return updateUnknown(state, action);
  }
};

type GetState = () => Model;

async function* fxGenerateTitle(
  getState: GetState,
  accessToken: string | undefined,
): AsyncGenerator<Action> {
  const { conversationTitle, conversationId, messages, agentId, projectId } =
    getState();
  if (
    conversationTitle !== "Untitled" ||
    !conversationId ||
    !projectId ||
    !agentId
  ) {
    return;
  }

  const userMessages = [...messages.values()].filter((m) => m.role === "user");
  const text = flattenUIMessagesToString(userMessages);
  const result = await generateTitle(accessToken, text);

  if (!result.ok) {
    logger.warn("Failed to generate title", { error: result.error });
    return;
  }

  yield { type: "set-conversation-info", title: result.value.title, agentId };

  const upsertResult = await upsertConversation(accessToken, conversationId, {
    project_id: projectId,
    agent_id: agentId,
    title: result.value.title,
  });

  if (!upsertResult.ok) {
    logger.error("Failed to persist conversation title", {
      error: upsertResult.error,
    });
  }
}

/**
 * Post-stream safety net: only summarizes thinking groups that weren't already
 * handled mid-stream (i.e., groups at indices >= existing summaries count).
 * This typically covers just the last thinking group, which can't be detected
 * as "closed" mid-stream since it's always the final segment.
 */
async function* fxGenerateThinkingSummaries(
  getState: GetState,
  accessToken: string | undefined,
): AsyncGenerator<Action> {
  const { messages, thinkingSummaries } = getState();
  const allMessages = [...messages.values()];
  const lastAssistant = allMessages.findLast((m) => m.role === "assistant");
  if (!lastAssistant) return;

  const segments = groupPartsIntoSegments(lastAssistant.parts);
  const thinkingGroups = segments.filter((s) => s.kind === "thinking");
  if (thinkingGroups.length === 0) return;

  const existing = thinkingSummaries.get(lastAssistant.id) ?? [];
  const remaining = thinkingGroups.slice(existing.length);

  if (remaining.length === 0) {
    // All groups were already summarized mid-stream. Persist them now
    // (the message is in the DB at this point, unlike during mid-stream).
    if (existing.some((s) => s !== "")) {
      await saveThinkingSummaries(accessToken, lastAssistant.id, existing);
    }
    return;
  }

  // Show placeholders for remaining groups
  const placeholders = [...existing, ...remaining.map(() => "")];
  yield {
    type: "set-thinking-summaries",
    messageId: lastAssistant.id,
    summaries: placeholders,
  };

  const inputs = remaining.map((g) =>
    formatThinkingGroupPartsForPrompt(g.parts),
  );

  const results = await Promise.allSettled(
    inputs.map((text) => generateThinkingSummary(accessToken, text)),
  );

  // Re-read current state to avoid overwriting concurrent mid-stream updates
  const fresh = [...(getState().thinkingSummaries.get(lastAssistant.id) ?? [])];
  for (let i = 0; i < results.length; i++) {
    const r = results[i];
    fresh[existing.length + i] =
      r.status === "fulfilled" && r.value.ok ? r.value.value.title : "";
  }
  const summaries = fresh;

  yield {
    type: "set-thinking-summaries",
    messageId: lastAssistant.id,
    summaries,
  };

  // Persist to database if we have real summaries
  if (summaries.some((s) => s !== "")) {
    await saveThinkingSummaries(accessToken, lastAssistant.id, summaries);
  }
}

async function* fxStreamMessageSummary(
  getState: GetState,
  action: StreamMessageAction,
): AsyncGenerator<Action> {
  const message = action.message;
  if (message.role !== "assistant") return;

  const segments = groupPartsIntoSegments(message.parts);
  const closedCount = countClosedThinkingGroups(segments);
  if (closedCount === 0) return;

  // How many summaries do we already have (or are in-flight) for this message?
  const existing = getState().thinkingSummaries.get(message.id) ?? [];
  if (closedCount <= existing.length) return;

  // Get the thinking groups that need summarizing
  const thinkingGroups = segments.filter((s) => s.kind === "thinking");
  const newGroups = thinkingGroups.slice(existing.length, closedCount);

  // Dispatch placeholder immediately for all new groups
  const placeholders = [...existing, ...newGroups.map(() => "")];
  yield {
    type: "set-thinking-summaries",
    messageId: message.id,
    summaries: placeholders,
  };

  // Generate summaries for each new group
  for (let i = 0; i < newGroups.length; i++) {
    const group = newGroups[i];
    const text = formatThinkingGroupPartsForPrompt(group.parts);
    const result = await generateThinkingSummary(action.accessToken, text);
    const summary = result.ok ? result.value.title : "";

    // Re-read current summaries to avoid overwriting concurrent updates
    const current = getState().thinkingSummaries.get(message.id) ?? [];
    const updated = [...current];
    updated[existing.length + i] = summary;
    yield {
      type: "set-thinking-summaries",
      messageId: message.id,
      summaries: updated,
    };
  }
  // Note: don't persist here — the message may not be in the DB yet.
  // Persistence happens in fxGenerateThinkingSummaries after the stream ends.
}

/** Extract thinking summaries from message metadata into store actions. */
function* hydrateThinkingSummaries(
  messages: ChatMessage[],
): Generator<SetThinkingSummariesAction> {
  for (const msg of messages) {
    if (msg.role !== "assistant") continue;
    const summaries = msg.metadata?.thinking_summaries;
    if (summaries && summaries.length > 0) {
      yield {
        type: "set-thinking-summaries",
        messageId: msg.id,
        summaries,
      };
    }
  }
}

async function* fxResumeConversation(
  getState: GetState,
  action: ResumeConversationAction,
): AsyncGenerator<Action> {
  yield* hydrateThinkingSummaries(action.messages);

  if (!action.submitMessage) {
    return;
  }

  const stream = await postUserMessage(
    action.accessToken,
    action.submitMessage,
  );

  for await (const message of stream) {
    yield { type: "stream-message", message, accessToken: action.accessToken };
    yield* fxFromMessage(message, getState);
  }

  yield { type: "set-loading", isLoading: false };
  yield* fxGenerateTitle(getState, action.accessToken);
  yield* fxGenerateThinkingSummaries(getState, action.accessToken);
}

async function* fxSetConversation(
  action: SetConversationAction,
): AsyncGenerator<Action> {
  if (!action.load) {
    return;
  }

  const result = await getMessages(action.accessToken, action.conversationId);

  if (result.ok) {
    yield {
      type: "set-conversation-info",
      title: result.value.title,
      agentId: result.value.agent_id,
    };
    yield {
      type: "set-messages",
      messages: result.value.messages,
    };

    yield* hydrateThinkingSummaries(result.value.messages);
  } else {
    yield {
      type: "error",
      message: "Couldn't load conversation",
      error: result.error,
    };
    yield { type: "set-loading", isLoading: false };
  }
}

/** Return tool call IDs from completed save-note tool calls in a message. */
export const getListInvalidatingToolCallIds = (
  message: ChatMessage,
): string[] => {
  const ids: string[] = [];
  for (const part of message.parts) {
    if (!("state" in part) || part.state !== "output-available") continue;
    if (part.type === "tool-saveNote") ids.push(part.toolCallId);
  }
  return ids;
};

/** Yield a note-list refresh only for tool calls we haven't seen yet. */
function* fxFromMessage(
  message: ChatMessage,
  getState: GetState,
): Generator<Action> {
  const ids = getListInvalidatingToolCallIds(message);
  if (ids.length === 0) return;

  const seen = getState().seenListInvalidatingToolCallIds;
  const unseen = ids.filter((id) => !seen.has(id));
  if (unseen.length === 0) return;

  yield { type: "add-list-invalidating-tool-call-ids", ids: unseen };
  yield { type: "request-load-note-lists" };
}

async function* fxSubmit(
  getState: GetState,
  action: SubmitAction,
): AsyncGenerator<Action> {
  if (isEmpty(action.content)) {
    return;
  }

  const state = getState();
  const { conversationId, projectId } = state;

  if (!conversationId) {
    yield {
      type: "error",
      message: "Conversation not set",
      error: "Cannot submit message without a conversation ID",
    };
    return;
  }

  if (!projectId) {
    yield {
      type: "error",
      message: "Project not set",
      error: "Cannot submit message without a project ID",
    };
    return;
  }

  // Create user message with client-generated ID
  const now = nowIsoString();
  const userMessage = parseUserChatMessage({
    role: "user",
    parts: [{ type: "text", text: action.content }],
    metadata: {
      project_id: projectId,
      conversation_id: conversationId,
      created_at: now,
      updated_at: now,
    },
  });

  yield { type: "append-messages", messages: [userMessage] };
  yield { type: "set-loading", isLoading: true };

  const stream = await postUserMessage(action.accessToken, userMessage);

  for await (const message of stream) {
    yield { type: "stream-message", message, accessToken: action.accessToken };
    yield* fxFromMessage(message, getState);
  }

  yield { type: "set-loading", isLoading: false };

  if (getState().conversationTitle === "Untitled") {
    yield* fxGenerateTitle(getState, action.accessToken);
  }
  yield* fxGenerateThinkingSummaries(getState, action.accessToken);
}

export const fx = async function* (
  getState: GetState,
  action: Action,
): AsyncGenerator<Action> {
  switch (action.type) {
    case "resume-conversation":
      return yield* fxResumeConversation(getState, action);
    case "set-conversation":
      return yield* fxSetConversation(action);
    case "stream-message":
      return yield* fxStreamMessageSummary(getState, action);
    case "submit":
      return yield* fxSubmit(getState, action);
    default:
      return;
  }
};

export type AiChatStore = Store<Model, Action>;
